import React, { useContext, useMemo, useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import { GET_JOURNAL_ENTRIES_QUERY, GET_INSPIRATIONS_QUERY, GET_CONSUMPTIONS_QUERY } from '../query/gqlQueries'
import styles from './HomeView.module.scss'
import d from './Dashboard.module.scss'
import { Shelf } from './WorkHome'

// Life's home, laid out like Communicate's: today's journal first (what to
// do next), then today's log and a line of inspiration side by side, then
// your recent entries — and the tools last. Colour means something: green
// for what's good for you, coral for what isn't.

const DAY = 24 * 60 * 60 * 1000
const dayKey = (v) => { const t = new Date(v); return isNaN(t) ? '' : `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}` }
const when = (v) => { const t = v ? new Date(v) : null; return t && !isNaN(t) ? t.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '' }
const clip = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n).trimEnd() + '…' : t }

export default function LifeHome({ run }) {
  const { username } = useContext(UserContext)
  const journalQ = useQuery(GET_JOURNAL_ENTRIES_QUERY, { variables: { username, limit: 40 }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const inspQ = useQuery(GET_INSPIRATIONS_QUERY, { variables: { username }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const since = useMemo(() => new Date(Date.now() - 7 * DAY).toISOString(), [])
  const consQ = useQuery(GET_CONSUMPTIONS_QUERY, { variables: { input: { startDate: since } }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })

  const entries = useMemo(() => [...(journalQ.data?.getJournalEntries || [])]
    .filter(e => !e.properties?.archived)
    .sort((a, b) => String(b.properties?.createdAt || '').localeCompare(String(a.properties?.createdAt || ''))), [journalQ.data])
  const last = entries[0]
  const todayKey = dayKey(Date.now())
  const wroteToday = last && dayKey(last.properties?.createdAt) === todayKey
  // The last seven days, oldest first: did you write?
  const week = useMemo(() => {
    const keys = new Set(entries.map(e => dayKey(e.properties?.createdAt)))
    return [...Array(7)].map((_, i) => {
      const t = new Date(Date.now() - (6 - i) * DAY)
      return { label: t.toLocaleDateString(undefined, { weekday: 'narrow' }), wrote: keys.has(dayKey(t)), today: i === 6 }
    })
  }, [entries])

  const consumptions = useMemo(() => consQ.data?.getConsumptions || [], [consQ.data])
  const today = consumptions.filter(c => dayKey(c.properties?.time) === todayKey)
  // Today's log, by thing: how much of each.
  const todayBy = useMemo(() => {
    const by = new Map()
    for (const c of today) {
      const p = c.properties || {}
      const k = `${p.name}|${p.units}`
      const e = by.get(k) || { name: p.name, units: p.units, amount: 0, emoji: p.emoji, good: p.good }
      e.amount += Number(p.amount) || 0
      by.set(k, e)
    }
    return [...by.values()]
  }, [today])

  // One line of inspiration, a new one every so often.
  const lines = inspQ.data?.getInspirations || []
  const [pick, setPick] = useState(0)
  useEffect(() => {
    if (lines.length < 2) return undefined
    setPick(Math.floor(Math.random() * lines.length))
    const id = setInterval(() => setPick(p => (p + 1) % lines.length), 20000)
    return () => clearInterval(id)
  }, [lines.length])
  const line = lines[pick % Math.max(lines.length, 1)]

  return (
    <div className={styles.grid}>
      {/* ── today's journal: the step to take ── */}
      <section className={`${styles.card} ${styles.cardNext} ${d.hero}`}>
        <div className={styles.cardHead}><span className={styles.cardLabel}>Today</span>
          <button className={styles.linkBtn} onClick={() => run('journal')}>Journal →</button></div>
        <div className={d.heroBody}>
          <div>
            <div className={d.heroTitle}>{wroteToday ? 'You’ve written today.' : 'How was today?'}</div>
            <div className={d.heroMeta}>{last ? `Last entry ${when(last.properties?.createdAt)}${last.properties?.mood ? ` · ${last.properties.mood}` : ''}` : 'Your first entry starts the habit.'}</div>
            {last && <div className={d.heroText}>{clip(last.properties?.title || last.properties?.body, 180)}</div>}
            <div className={d.week} aria-label="The last seven days">
              {week.map((w, i) => (
                <span key={i} className={`${d.weekDay} ${w.wrote ? d.weekOn : ''} ${w.today ? d.weekToday : ''}`}><i />{w.label}</span>
              ))}
            </div>
          </div>
          <button className={d.primary} onClick={() => run(wroteToday ? 'journal' : 'addJournal')}>{wroteToday ? 'Read it →' : 'Write today’s entry'}</button>
        </div>
      </section>

      {/* ── today's log ── */}
      <section className={styles.card}>
        <div className={styles.cardHead}><span className={styles.cardLabel}>Had today</span>
          <button className={styles.linkBtn} onClick={() => run('addConsumption')}>Log →</button></div>
        {todayBy.length === 0
          ? <div className={styles.muted}>Nothing logged today.</div>
          : todayBy.map((c, i) => (
            <div key={i} className={`${d.logRow} ${c.good === true ? d.good : c.good === false ? d.bad : ''}`}>
              <span className={d.logEmoji}>{c.emoji || '•'}</span>
              <span className={d.logName}>{c.name}</span>
              <span className={d.logAmount}>{+c.amount.toFixed(2)} {c.units}</span>
            </div>
          ))}
        <div className={d.logNote}>{consumptions.length} logged in the last seven days</div>
      </section>

      {/* ── a line to carry ── */}
      <section className={styles.card}>
        <div className={styles.cardHead}><span className={styles.cardLabel}>Inspiration</span>
          <button className={styles.linkBtn} onClick={() => run('addInspiration')}>Add →</button></div>
        {line
          ? <blockquote className={d.quote}>{line.properties?.text}</blockquote>
          : <div className={styles.muted}>Save a line that moves you — it shows up here.</div>}
        {lines.length > 1 && <button className={styles.linkBtn} onClick={() => setPick(p => (p + 1) % lines.length)}>Another →</button>}
      </section>

      {/* ── recent entries ── */}
      {entries.length > 1 && (
        <div className={styles.cardWide}>
          <Shelf title="Recent entries" action={{ label: 'All →', onClick: () => run('journal') }}>
            {entries.slice(0, 10).map(e => (
              <button key={e.identity} className={d.tile} onClick={() => run('journal')}>
                <span className={d.tileTop}><span className={d.tag}>{when(e.properties?.createdAt)}</span>{e.properties?.mood && <span className={d.tag}>{e.properties.mood}</span>}</span>
                <span className={d.tileTitle}>{clip(e.properties?.title || e.properties?.body, 70) || 'Untitled'}</span>
                {e.properties?.title && e.properties?.body && <span className={d.tileMeta}>{clip(e.properties.body, 90)}</span>}
              </button>
            ))}
          </Shelf>
        </div>
      )}
    </div>
  )
}
